import { useState } from 'react'
import { Loader2, XCircle, Download } from 'lucide-react'
import { useMdmCommands, useCancelMdmCommand } from '../../../hooks/useMdm'
import { mdmApi } from '../../../services/mdmApi'
import { formatDateTimeSeconds } from '../../../utils/format'
import { CommandStatusBadge } from './mobileUi'
import { COMMAND_LABELS, errorMessage, saveBlob, messageReadLabel } from './mobileUtils'

function summarize(value) {
  if (value == null) return null
  if (typeof value === 'string') return value
  const s = JSON.stringify(value)
  return s === '{}' ? null : s
}

/** Histórico de comandos do device (polling rápido enquanto houver comando em andamento). */
export default function CommandsTab({ deviceId, readOnly }) {
  const { data: commands = [], isLoading, error } = useMdmCommands(deviceId, 100)
  const cancel = useCancelMdmCommand(deviceId)
  const [msg, setMsg] = useState(null)

  const download = async (fileId, name) => {
    setMsg(null)
    try {
      const blob = await mdmApi.downloadFile(deviceId, fileId)
      saveBlob(blob, name)
    } catch (e) {
      setMsg(errorMessage(e, 'Falha ao baixar o arquivo.'))
    }
  }

  const handleCancel = async (id) => {
    setMsg(null)
    try {
      await cancel.mutateAsync(id)
    } catch (e) {
      setMsg(errorMessage(e, 'Não foi possível cancelar.'))
    }
  }

  if (isLoading) {
    return <div className="py-8 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Carregando comandos...</div>
  }

  return (
    <div className="space-y-2">
      {error && <p className="text-sm text-red-600">{errorMessage(error, 'Erro ao carregar comandos.')}</p>}
      {msg && <p className="text-sm text-red-600">{msg}</p>}
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2">Comando</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Criado</th>
              <th className="text-left px-3 py-2">Concluído</th>
              <th className="text-left px-3 py-2">Por</th>
              <th className="text-left px-3 py-2">Resultado</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {commands.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">Nenhum comando enviado ainda.</td></tr>
            ) : commands.map((c) => {
              const payload = summarize(c.payload)
              const read = c.type === 'message' && !c.error ? messageReadLabel(c) : null
              const result = c.error || (read ? null : summarize(c.result))
              const fileId = c.result?.fileId
              return (
                <tr key={c.id} className="align-top hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">
                      {COMMAND_LABELS[c.type] || c.type}
                      {c.type === 'message' && c.payload?.priority === 'high' && (
                        <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-normal bg-red-100 text-red-800">Prioridade alta</span>
                      )}
                    </div>
                    {payload && <div className="text-xs text-gray-500 font-mono break-all max-w-xs">{payload}</div>}
                  </td>
                  <td className="px-3 py-2"><CommandStatusBadge status={c.status} /></td>
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDateTimeSeconds(c.createdAt)}</td>
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{c.completedAt ? formatDateTimeSeconds(c.completedAt) : '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{c.requestedBy || '—'}</td>
                  <td className="px-3 py-2">
                    {read && (
                      <div className={`text-xs ${read.ok ? 'text-green-700' : 'text-gray-600'}`}>{read.text}</div>
                    )}
                    {result && (
                      <div className={`text-xs font-mono break-all max-w-sm ${c.error ? 'text-red-600' : 'text-gray-600'}`}>{result}</div>
                    )}
                    {fileId && (
                      <button type="button" onClick={() => download(fileId, c.type === 'screenshot' ? `screenshot-${c.id}.jpg` : (c.payload?.path || '').split('/').pop() || `arquivo-${c.id}`)}
                        className="mt-1 inline-flex items-center gap-1 text-xs text-theme-primary hover:underline">
                        <Download className="w-3.5 h-3.5" /> Baixar arquivo
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!readOnly && c.status === 'Pending' && (
                      <button type="button" onClick={() => handleCancel(c.id)} disabled={cancel.isPending}
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600" title="Cancelar">
                        <XCircle className="w-4 h-4" /> Cancelar
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
